import type { TaskBusinessState, TaskResponseDto } from '@/types/task';

/** Left open when a device is set inactive. Same exceptions patient deactivate already uses. */
export const PRESERVED_DEACTIVATE_TASK_CODES = new Set([
  'transmission_tracking',
  'check_in',
  'report_upload',
  'report_upload_with_encounter'
]);

const OPEN_TASK_STATES = new Set<TaskBusinessState>(['open', 'in_progress', 'on_hold']);

export const DEVICE_INACTIVE_TASK_CANCEL_REASON = 'Device was set inactive.';

export function isPreservedDeactivateTask(task: {
  taskType?: { code?: string | null } | null;
}): boolean {
  return PRESERVED_DEACTIVATE_TASK_CODES.has(task.taskType?.code ?? '');
}

/**
 * Open work to cancel after one device is set inactive.
 * Tasks for a different device stay open. Tasks with no device are included.
 */
export function tasksToCancelOnDeviceDeactivate(
  tasks: TaskResponseDto[],
  deviceId: string
): TaskResponseDto[] {
  if (!deviceId) return [];
  return tasks.filter((task) => {
    if (!OPEN_TASK_STATES.has(task.businessState)) return false;
    if (isPreservedDeactivateTask(task)) return false;
    if (task.matchDeviceId && task.matchDeviceId !== deviceId) return false;
    return true;
  });
}

type ListOpenTasks = (params: {
  patientId: string;
  businessStates: TaskBusinessState[];
  limit: number;
  skip: number;
}) => { unwrap: () => Promise<{ items: TaskResponseDto[] }> };

export async function loadOpenPatientTasks(
  listTasks: ListOpenTasks,
  patientId: string
): Promise<TaskResponseDto[]> {
  const all: TaskResponseDto[] = [];
  let skip = 0;
  for (let page = 0; page < 10; page += 1) {
    const result = await listTasks({
      patientId,
      businessStates: ['open', 'in_progress', 'on_hold'],
      limit: 100,
      skip
    }).unwrap();
    all.push(...result.items.filter((task) => OPEN_TASK_STATES.has(task.businessState)));
    if (result.items.length < 100) break;
    skip += 100;
  }
  return all;
}
